import { REWARDS_SOCIAL_SHARE_CHANNEL_TYPES } from "@apps/rewards/constants/rewardsSocialShareTypes";
import { DiscountRewardSchema } from "@apps/rewards/schemas/RewardsRewardSchemas/RewardsDiscountRewardSchema";
import { FreeProductRewardSchema } from "@apps/rewards/schemas/RewardsRewardSchemas/RewardsFreeProductRewardSchema";
import { GiftCardRewardSchema } from "@apps/rewards/schemas/RewardsRewardSchemas/RewardsGiftCardRewardSchema";
import { PointsRewardSchema } from "@apps/rewards/schemas/RewardsRewardSchemas/RewardsPointsRewardSchema";
import { StoreCreditRewardSchema } from "@apps/rewards/schemas/RewardsRewardSchemas/RewardsStoreCreditRewardSchema";
import {
    array,
    assign,
    boolean,
    enums,
    literal,
    nullable,
    number,
    object,
    omit,
    string,
    union,
} from "superstruct";

import { RewardsIconSchema } from "../../../schemas/RewardsIconSchema";
import {
    REWARDS_BUY_X_TO_GET_Y_OBJECT_TYPES,
    REWARDS_EARNING_RULE_TYPES,
} from "../constants";

export const RewardsPlaceOrderPointsRewardSchema = assign(
    PointsRewardSchema,
    object({
        amountSpent: number(),
    })
);

export const RewardsPlaceOrderStoreCreditRewardSchema = assign(
    StoreCreditRewardSchema,
    object({
        amountSpent: number(),
    })
);

export const RewardsEarningRulesRewardSchema = union([
    RewardsPlaceOrderPointsRewardSchema,
    RewardsPlaceOrderStoreCreditRewardSchema,
    PointsRewardSchema,
    omit(DiscountRewardSchema, ["discountCode"]),
    omit(GiftCardRewardSchema, ["code", "balance"]),
    FreeProductRewardSchema,
]);

const RewardsEarningRuleBaseSchema = object({
    id: number(),
    title: string(),
    description: nullable(string()),
    rewarding: RewardsEarningRulesRewardSchema,
    position: number(),
    icon: nullable(RewardsIconSchema),
    completed: boolean(),
    createdAt: string(),
    rewardDelayDays: nullable(number()),
});

const RewardsEarningRuleRewardLimitSchema = object({
    timesActionLimitCount: nullable(number()),
    timesActionLimitPeriod: nullable(number()),
});

const RewardsSocialShareChannelsSchema = object({
    channelName: enums(Object.values(REWARDS_SOCIAL_SHARE_CHANNEL_TYPES)),
    isEnabled: boolean(),
});

const RewardsCreateAccountEarningRuleSchema = assign(
    RewardsEarningRuleBaseSchema,
    object({
        type: literal(REWARDS_EARNING_RULE_TYPES.createAccount),
    })
);

const RewardsPlaceOrderEarningRuleSchema = assign(
    RewardsEarningRuleBaseSchema,
    RewardsEarningRuleRewardLimitSchema,
    object({
        type: literal(REWARDS_EARNING_RULE_TYPES.placeOrder),
        minimumPurchaseAmount: nullable(number()),
    })
);

const RewardsLeaveReviewEarningRuleSchema = assign(
    RewardsEarningRuleBaseSchema,
    RewardsEarningRuleRewardLimitSchema,
    object({
        type: literal(REWARDS_EARNING_RULE_TYPES.leaveReview),
        extraPointsForPhoto: nullable(number()),
    })
);

export const RewardsNewsLetterEarningRuleSchema = assign(
    RewardsEarningRuleBaseSchema,
    object({
        type: literal(REWARDS_EARNING_RULE_TYPES.newsletterSubscribe),
        emailSubscription: boolean(),
        smsSubscription: boolean(),
    })
);

export const RewardsBirthdayGiftEarningRuleSchema = assign(
    omit(RewardsEarningRuleBaseSchema, ["rewardDelayDays"]),
    object({
        type: literal(REWARDS_EARNING_RULE_TYPES.birthdayGift),
        pointsMultiplier: nullable(number()),
        forbidCustomersToEditBirthday: boolean(),
    })
);

const RewardsInstagramFollowEarningRuleSchema = assign(
    RewardsEarningRuleBaseSchema,
    object({
        type: literal(REWARDS_EARNING_RULE_TYPES.instagramFollow),
        instagramAccount: string(),
    })
);

const RewardsFacebookPageVisitEarningRuleSchema = assign(
    RewardsEarningRuleBaseSchema,
    object({
        type: literal(REWARDS_EARNING_RULE_TYPES.facebookPageVisit),
        facebookPageName: string(),
    })
);

const RewardsFacebookShareEarningRuleSchema = assign(
    RewardsEarningRuleBaseSchema,
    object({
        type: literal(REWARDS_EARNING_RULE_TYPES.facebookShare),
        shareLink: string(),
    })
);

const RewardsTwitterFollowEarningRuleSchema = assign(
    RewardsEarningRuleBaseSchema,
    object({
        type: literal(REWARDS_EARNING_RULE_TYPES.twitterFollow),
        twitterAccount: string(),
    })
);

const RewardsShareToTwitterEarningRuleSchema = assign(
    RewardsEarningRuleBaseSchema,
    object({
        type: literal(REWARDS_EARNING_RULE_TYPES.twitterShare),
        shareMessage: string(),
    })
);

const RewardsPunchCardEarningRuleSchema = assign(
    RewardsEarningRuleBaseSchema,
    RewardsEarningRuleRewardLimitSchema,
    object({
        type: literal(REWARDS_EARNING_RULE_TYPES.punchCard),
        numberOfOrders: number(),
        minimumPurchaseAmount: nullable(number()),
    })
);

const RewardsGoalSpendEarningRuleSchema = assign(
    RewardsEarningRuleBaseSchema,
    RewardsEarningRuleRewardLimitSchema,
    object({
        type: literal(REWARDS_EARNING_RULE_TYPES.goalSpend),
        spendingGoal: number(),
    })
);

export const RewardsSocialShareEarningRuleSchema = assign(
    RewardsEarningRuleBaseSchema,
    RewardsEarningRuleRewardLimitSchema,
    object({
        type: literal(REWARDS_EARNING_RULE_TYPES.socialShare),
        message: string(),
        channels: array(RewardsSocialShareChannelsSchema),
    })
);

const RewardsBuyXToGetYEarningRuleSchema = assign(
    RewardsEarningRuleBaseSchema,
    RewardsEarningRuleRewardLimitSchema,
    object({
        type: literal(REWARDS_EARNING_RULE_TYPES.buyXToGetY),
        objectType: enums(Object.values(REWARDS_BUY_X_TO_GET_Y_OBJECT_TYPES)),
        objectId: number(),
        objectTitle: string(),
        objectHandle: string(),
    })
);

const RewardsCustomActionEarningRuleSchema = assign(
    RewardsEarningRuleBaseSchema,
    RewardsEarningRuleRewardLimitSchema,
    object({
        type: literal(REWARDS_EARNING_RULE_TYPES.customAction),
    })
);

const RewardsFollowOnYouTubeEarningRuleSchema = assign(
    RewardsEarningRuleBaseSchema,
    object({
        type: literal(REWARDS_EARNING_RULE_TYPES.followOnYouTube),
        youtubeChannel: string(),
    })
);

const RewardsFollowOnCustomEarningRuleSchema = assign(
    RewardsEarningRuleBaseSchema,
    object({
        type: literal(REWARDS_EARNING_RULE_TYPES.followOnCustom),
        customChannelName: string(),
        customChannelUrl: string(),
    })
);

const RewardsFollowOnTikTokEarningRuleSchema = assign(
    RewardsEarningRuleBaseSchema,
    object({
        type: literal(REWARDS_EARNING_RULE_TYPES.followOnTikTok),
        tiktokAccount: string(),
    })
);

export const RewardsEarningRulesSchema = union([
    RewardsCreateAccountEarningRuleSchema,
    RewardsPlaceOrderEarningRuleSchema,
    RewardsLeaveReviewEarningRuleSchema,
    RewardsNewsLetterEarningRuleSchema,
    RewardsBirthdayGiftEarningRuleSchema,
    RewardsInstagramFollowEarningRuleSchema,
    RewardsFacebookPageVisitEarningRuleSchema,
    RewardsFacebookShareEarningRuleSchema,
    RewardsTwitterFollowEarningRuleSchema,
    RewardsShareToTwitterEarningRuleSchema,
    RewardsPunchCardEarningRuleSchema,
    RewardsGoalSpendEarningRuleSchema,
    RewardsSocialShareEarningRuleSchema,
    RewardsBuyXToGetYEarningRuleSchema,
    RewardsCustomActionEarningRuleSchema,
    RewardsFollowOnYouTubeEarningRuleSchema,
    RewardsFollowOnTikTokEarningRuleSchema,
    RewardsFollowOnCustomEarningRuleSchema,
]);

export const RewardsEarningRulesResponseSchema = object({
    rules: array(RewardsEarningRulesSchema),
});
